import { useEffect, useState } from 'react';
import { supabase } from '../supabase';

function isDismissedSettingValue(value: unknown): boolean {
    return typeof value === 'object'
        && value !== null
        && !Array.isArray(value)
        && (value as { dismissed?: unknown }).dismissed === true;
}

export function useUserSettingDismissal(settingKey: string) {
    const [isDismissed, setIsDismissed] = useState<boolean | null>(null);

    useEffect(() => {
        let isMounted = true;

        async function loadDismissal() {
            try {
                const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
                if (sessionError) {
                    throw sessionError;
                }

                const userId = sessionData.session?.user.id;
                if (!userId) {
                    if (isMounted) {
                        setIsDismissed(false);
                    }
                    return;
                }

                const { data, error: settingsError } = await supabase
                    .from('settings')
                    .select('value')
                    .eq('profile_id', userId)
                    .eq('setting_key', settingKey)
                    .maybeSingle();

                if (settingsError) {
                    throw settingsError;
                }

                if (isMounted) {
                    setIsDismissed(isDismissedSettingValue(data?.value));
                }
            } catch (err) {
                console.error(`Failed to load dismissal setting ${settingKey}:`, err);
                if (isMounted) {
                    setIsDismissed(false);
                }
            }
        }

        loadDismissal();

        return () => {
            isMounted = false;
        };
    }, [settingKey]);

    const dismiss = async () => {
        setIsDismissed(true);

        try {
            const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
            if (sessionError) {
                throw sessionError;
            }

            const userId = sessionData.session?.user.id;
            if (!userId) {
                return;
            }

            const value = {
                dismissed: true,
                dismissed_at: new Date().toISOString(),
            };

            const { data: existing, error: selectError } = await supabase
                .from('settings')
                .select('id')
                .eq('profile_id', userId)
                .eq('setting_key', settingKey)
                .maybeSingle();

            if (selectError) {
                throw selectError;
            }

            if (existing?.id) {
                const { error: updateError } = await supabase
                    .from('settings')
                    .update({ value })
                    .eq('id', existing.id);

                if (updateError) {
                    throw updateError;
                }
                return;
            }

            const { error: insertError } = await supabase
                .from('settings')
                .insert({
                    profile_id: userId,
                    setting_key: settingKey,
                    value,
                });

            if (insertError) {
                throw insertError;
            }
        } catch (err) {
            console.error(`Failed to save dismissal setting ${settingKey}:`, err);
        }
    };

    return { isDismissed, dismiss };
}
